import styled from '@emotion/styled';

import { screen } from '@/components/common/breakpoints';
import { HeadingLevel } from '@/components/common/MarkUp';
import { Surface } from '@/components/common/Surface';
import { Title } from '@/components/common/Title';
import { TitleWithIcon } from '@/components/common/TitleWithIcon';
import { Stack } from '@/components/layout/Stack';

import type { IconSet } from '@/components/common/Icon';
import type { Heading } from '@/types/shared';

type CardGridProps = {
  heading: Heading;
  cards?: Array<{
    _key: string;
    _type: 'card';
    title?: {
      _type: 'title';
      heading?: Heading;
      subheading?: string;
    };
    icon?: {
      _type: 'icon';
      name?: IconSet;
    };
  }>;
};

export const CardGrid = ({ heading, cards }: CardGridProps) => {
  return (
    <Stack spacing="--space-component-xxl">
      <HeadingLevel>
        <HeadingWrapper>
          <Title variant="block" size="md" heading={heading} />
        </HeadingWrapper>
        <GridContainer>
          {cards?.map((card) => (
            <Surface
              key={card._key}
              surfaceColour="--surface-warm-base"
              borderRadius="--radius-component-xl"
              padding="--space-component-xl"
            >
              <HeadingLevel>
                <TitleWithIcon
                  variant="block"
                  size="sm"
                  iconVariant={{ lg: 'large', base: 'standard' }}
                  iconName={card.icon.name}
                  heading={card.title?.heading}
                  subheading={card.title?.subheading}
                  alignment="top"
                  mobileAlignment="left"
                />
              </HeadingLevel>
            </Surface>
          ))}
        </GridContainer>
      </HeadingLevel>
    </Stack>
  );
};

const HeadingWrapper = styled.div`
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-weight: var(--font-weight-semibold);
  }
`;

const GridContainer = styled.div`
  display: grid;
  gap: var(--space-component-lg);
  grid-template-columns: 1fr;
  grid-auto-rows: 1fr;

  ${screen.sm} {
    grid-template-columns: 1fr 1fr;
    gap: var(--space-component-xl);
  }
`;
